import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Phone,
  MessageCircle,
  FileText,
  Settings,
  Smile,
  HeartHandshake,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";

const ProcessPage = () => {
  const steps = [
    {
      number: "01",
      icon: MessageCircle,
      title: "Évaluation et discussion de vos besoins",
      description: "Lors de notre première rencontre, nous prenons le temps d'écouter vos préoccupations, d'examiner votre situation dentaire et de comprendre vos attentes. Cette étape est essentielle pour personnaliser votre traitement.",
      details: ["Examen complet sans frais", "Écoute de vos besoins", "Discussion ouverte et sans pression"]
    },
    {
      number: "02",
      icon: FileText,
      title: "Explications simples et transparentes",
      description: "Nous vous expliquons clairement les options disponibles, les avantages de chaque solution et le déroulement du traitement. Aucune question n'est superflue.",
      details: ["Explications sans jargon", "Réponses à toutes vos questions", "Plan de traitement détaillé"]
    },
    {
      number: "03",
      icon: Settings,
      title: "Prise d'empreintes et conception",
      description: "Nous procédons à la prise d'empreintes précises de votre bouche pour concevoir une prothèse parfaitement adaptée à votre anatomie.",
      details: ["Empreintes de haute précision", "Conception sur mesure", "Choix des teintes naturelles"]
    },
    {
      number: "04",
      icon: Smile,
      title: "Ajustements précis",
      description: "Nous effectuons plusieurs essais pour nous assurer que la prothèse s'adapte parfaitement, est confortable et esthétiquement naturelle.",
      details: ["Essayages multiples", "Ajustements minutieux", "Validation du confort"]
    },
    {
      number: "05",
      icon: HeartHandshake,
      title: "Suivi après installation",
      description: "Notre accompagnement ne s'arrête pas à la livraison. Nous restons disponibles pour tout ajustement nécessaire et pour répondre à vos questions.",
      details: ["Suivi personnalisé", "Ajustements inclus", "Support continu"]
    }
  ];

  return (
    <div className="bg-[#F5F5F0]" data-testid="process-page">
      {/* Hero Section */}
      <section className="bg-white section-padding" data-testid="process-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-playfair heading-xl text-slate-900 mb-6">
              Un accompagnement clair, étape par étape
            </h1>
            <p className="text-body-lg">
              Nous croyons qu'un patient bien informé est un patient en confiance. 
              Découvrez comment nous vous accompagnons tout au long du processus.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding" data-testid="process-steps">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative mb-16 last:mb-0"
                data-testid={`process-step-${index}`}
              >
                {/* Connection Line */}
                {index < steps.length - 1 && (
                  <div className="absolute left-8 top-24 w-0.5 h-[calc(100%-3rem)] bg-stone-200 hidden md:block" />
                )}
                
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Step Number */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-slate-900 flex items-center justify-center">
                      <span className="heading-playfair text-xl font-bold text-white">
                        {step.number}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="card flex-1 p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="icon-circle bg-[#F5F5F0]">
                        <step.icon className="w-5 h-5 text-slate-900" />
                      </div>
                      <h3 className="heading-playfair text-xl font-semibold text-slate-900">
                        {step.title}
                      </h3>
                    </div>
                    
                    <p className="text-body mb-6 leading-relaxed">
                      {step.description}
                    </p>

                    <ul className="space-y-2">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Message */}
      <section className="bg-white section-padding" data-testid="process-trust">
        <div className="container-custom">
          <div className="bg-slate-900 rounded-3xl p-8 md:p-12 lg:p-16 text-center">
            <h2 className="heading-playfair text-2xl md:text-3xl text-white mb-6">
              Nous croyons qu'un patient bien informé est un patient en confiance
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-8">
              À chaque étape, nous prenons le temps de vous expliquer ce qui se passe 
              et de répondre à toutes vos questions. Votre confort et votre compréhension 
              sont nos priorités.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="process-cta">
                <Button className="btn-call text-base px-8 py-6">
                  Commencer le processus
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <a 
                href="tel:450-441-3636" 
                className="btn-secondary bg-transparent border-white text-white hover:bg-white hover:text-slate-900 text-base px-8 py-6"
                data-testid="process-call"
              >
                <Phone className="w-5 h-5" />
                <span>450-441-3636</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="section-padding" data-testid="process-faq">
        <div className="container-custom text-center">
          <h2 className="heading-playfair heading-lg text-slate-900 mb-4">
            Des questions sur notre processus?
          </h2>
          <p className="text-body-lg max-w-2xl mx-auto mb-8">
            N'hésitez pas à nous contacter. Nous sommes là pour répondre 
            à toutes vos interrogations et vous guider à chaque étape.
          </p>
          <Link to="/contact" data-testid="process-contact-cta">
            <Button className="btn-primary text-base px-8 py-6">
              Nous contacter
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProcessPage;
